import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "select", "section",
    "industryInput", "genderInput", "ageInput", "heightInput", "surfaceInput", "poseInput", "poseGroup", "poseDirectiveList", "sourceModeInput", "sourceModeBlock",
    "foodModeInput", "culinaryCategoryInput", "bgSection", "bgGrid", "bgToggle", "accSection", "accGrid", "accToggle",
    "promptInput", "regenerateBtn", "cameraShotInput", "editorialPoseBlock",
    "heroBadge", "heroBadgeLabel"
  ]

  // Master suggestions — pre-baked from the rake compile step. Maps
  // category id → { backgrounds: [BG-XX...], accessories: [ACC-XX...] }
  // The view passes these via data-* values once we wire the dispatcher;
  // until then suggestions only highlight if a category preset is given.

  static values = { suggestUrl: String }

  static KID_GENDERS = ["boy", "girl"]

  connect() {
    this.render(this.selectTarget.value)
    this.filterPoses()
    this.filterSourceMode()
    this.foodModeChanged()
    // Apply suggestion highlighting if a category was preselected on load.
    const ccInput = this.hasCulinaryCategoryInputTarget ? this.culinaryCategoryInputTarget : null
    if (ccInput && ccInput.value) this.culinaryCategoryChanged({ target: ccInput })
  }

  switch(event) {
    const industry = event.target.value
    this.render(industry)

    // Industry-specific FORMAT default — real estate is 16:9 landscape, food is
    // 3:2 landscape. Resolution is left alone: it is the operator's call and
    // has nothing to do with which industry the shot is for.
    const formatSelect = this.element.querySelector("select[name='single_generation[image_format]']")
    if (formatSelect) {
      if (industry === "real_estate") {
        formatSelect.value = "16:9"
      } else if (industry === "food") {
        if (formatSelect.value !== "3:2") formatSelect.value = "3:2"
      }
      formatSelect.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.regenerateSuggestion()
  }

  sourceModeChanged() {
    this.filterSourceMode()
    // Whenever the source mode changes, re-evaluate the editorial clothes
    // sub-blocks too so they show/hide correctly when "editorial" is picked
    // with a previously-saved clothes_mode.
    this.applyEditorialClothesMode()
    this.regenerateSuggestion()
  }

  // Editorial mode has a sub-radio for "Clothes path" (flatlays vs brand).
  // We toggle the matching sub-block based on which radio is selected.
  editorialClothesChanged() {
    this.applyEditorialClothesMode()
  }

  // Editorial: reveal the separate pose-reference upload when "Separate pose
  // reference" is picked; hide + clear it otherwise (so a leftover file isn't
  // submitted after switching back to the combined background+pose mode).
  editorialPoseModeChanged(event) {
    const separate = event.target.value === "separate" && event.target.checked
    if (!this.hasEditorialPoseBlockTarget) return
    this.editorialPoseBlockTarget.hidden = !separate
    if (!separate) {
      const fileInput = this.editorialPoseBlockTarget.querySelector('input[type="file"]')
      if (fileInput) fileInput.value = ""
    }
  }

  applyEditorialClothesMode() {
    const mode = this.checkedRadioByName("single_generation[editorial_clothes_mode]")
    document.querySelectorAll("[data-editorial-clothes]").forEach((el) => {
      this.toggleVisibility(el, el.dataset.editorialClothes === mode)
    })
  }

  checkedRadioByName(name) {
    const radio = document.querySelector(`input[type=radio][name="${name}"]:checked`)
    return radio ? radio.value : null
  }

  // Food source mode — toggle every block tagged with `data-food-mode-only`
  // so the form swaps between picture-driven and text-driven layouts. Each
  // food_reference row uses the same flag inside the partial to swap thumb /
  // file inputs (ENHANCING) vs plate_id + plate_description (CREATION).
  foodModeChanged() {
    const mode = this.checkedRadioValue(this.foodModeInputTargets) || "enhancing"
    document.querySelectorAll('[data-food-mode-only]').forEach((el) => {
      const allowed = (el.dataset.foodModeOnly || "").split(/\s+/).filter(Boolean)
      this.toggleVisibility(el, allowed.includes(mode))
    })

    // In CREATION mode every plate is implicitly "used as ref" — auto-check
    // every row's `selected` checkbox so food_fanout! sees them all and the
    // restaurant fans out fully (root → all other plates).
    if (mode === "creation") {
      document.querySelectorAll('input[type="checkbox"][data-food-row-selected]').forEach((cb) => {
        cb.checked = true
      })
    }
  }

  // Layer 1 — culinary category change. Progressive disclosure:
  //   • Layer 2 + Layer 3 stay hidden until a category is picked.
  //   • When picked, only the recommended (affinity-matched) BG/ACC cards
  //     reveal — sorted by affinity overlap, suggested ones get a yellow ring.
  //   • A "+ Show all" toggle expands the rest.
  culinaryCategoryChanged(event) {
    const id  = event?.target?.value || ""
    const map = this.piktizSuggestions()
    const sug = map[id] || { backgrounds: [], accessories: [] }

    const bgPicked  = id !== ""
    const accPicked = id !== ""

    if (this.hasBgSectionTarget) this.toggleVisibility(this.bgSectionTarget, bgPicked)
    if (this.hasAccSectionTarget) this.toggleVisibility(this.accSectionTarget, accPicked)

    this._suggestedBackgrounds = sug.backgrounds || []
    this._suggestedAccessories = sug.accessories || []
    this._showAllBackgrounds = false
    this._showAllAccessories = false
    if (this.hasBgToggleTarget) this.bgToggleTarget.textContent = "+ Show all 20"
    if (this.hasAccToggleTarget) this.accToggleTarget.textContent = "+ Show all 40"

    this.applyLayerFilters()
    this.reorderLayerGrids()
  }

  toggleAllBackgrounds(event) {
    event.preventDefault()
    this._showAllBackgrounds = !this._showAllBackgrounds
    if (this.hasBgToggleTarget) {
      this.bgToggleTarget.textContent = this._showAllBackgrounds ? "− Show only recommended" : "+ Show all 20"
    }
    this.applyLayerFilters()
  }

  toggleAllAccessories(event) {
    event.preventDefault()
    this._showAllAccessories = !this._showAllAccessories
    if (this.hasAccToggleTarget) {
      this.accToggleTarget.textContent = this._showAllAccessories ? "− Show only recommended" : "+ Show all 40"
    }
    this.applyLayerFilters()
  }

  applyLayerFilters() {
    const bgSug = this._suggestedBackgrounds || []
    const accSug = this._suggestedAccessories || []

    document.querySelectorAll("[data-bg-card-wrapper]").forEach((w) => {
      const id   = w.dataset.bgCardWrapper
      const isSug = bgSug.includes(id)
      const card  = w.querySelector(".bg-card")
      if (card) card.classList.toggle("suggested", isSug)
      // Show suggested OR (show-all is on) OR (already-checked by operator).
      const cb = w.querySelector(".bg-checkbox")
      const checked = cb && cb.checked
      w.hidden = !(isSug || this._showAllBackgrounds || checked)
    })

    document.querySelectorAll("[data-acc-card-wrapper]").forEach((w) => {
      const id   = w.dataset.accCardWrapper
      const isSug = accSug.includes(id)
      const card  = w.querySelector(".acc-card")
      if (card) card.classList.toggle("suggested", isSug)
      const cb = w.querySelector(".acc-checkbox")
      const checked = cb && cb.checked
      w.hidden = !(isSug || this._showAllAccessories || checked)
    })
  }

  // Reorder grid items so suggested cards come first (visual hierarchy).
  reorderLayerGrids() {
    const sortGrid = (gridEl, datasetKey, sug) => {
      if (!gridEl) return
      const wrappers = Array.from(gridEl.children).filter((c) => c.dataset[datasetKey])
      wrappers.sort((a, b) => {
        const ai = sug.indexOf(a.dataset[datasetKey])
        const bi = sug.indexOf(b.dataset[datasetKey])
        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi)
      })
      wrappers.forEach((w) => gridEl.appendChild(w))
    }

    sortGrid(this.hasBgGridTarget ? this.bgGridTarget : null, "bgCardWrapper", this._suggestedBackgrounds || [])
    sortGrid(this.hasAccGridTarget ? this.accGridTarget : null, "accCardWrapper", this._suggestedAccessories || [])
  }

  // Lazy-parse the embedded suggestion map.
  piktizSuggestions() {
    if (this._piktizSuggestions !== undefined) return this._piktizSuggestions
    const node = document.querySelector("[data-piktiz-culinary-suggestions]")
    try {
      this._piktizSuggestions = node ? JSON.parse(node.textContent) : {}
    } catch (e) {
      console.warn("[industry-toggle] failed to parse piktiz culinary suggestions", e)
      this._piktizSuggestions = {}
    }
    return this._piktizSuggestions
  }

  // Gender change: filter poses + apply audience-appropriate defaults
  // (age, height) + regenerate the prompt body so "woman" / "boy" / etc. and
  // the 28y/1.80m vs 7y/1.25m phrasing stay consistent.
  genderChanged(event) {
    this.filterPoses()

    const gender = event?.target?.value || ""
    const isKid  = this.constructor.KID_GENDERS.includes(gender)

    const defaults = isKid
      ? { age: "7",  height: "125" }
      : { age: "28", height: "180" }

    if (this.hasAgeInputTarget && this.ageInputTargets[0]) {
      this.ageInputTargets[0].value = defaults.age
    }
    if (this.hasHeightInputTarget && this.heightInputTargets[0]) {
      this.heightInputTargets[0].value = defaults.height
    }

    this.regenerateSuggestion()
  }

  filterSourceMode() {
    const mode = this.checkedRadioValue(this.sourceModeInputTargets) || "brand_worn"
    if (!this.hasSourceModeBlockTarget) return
    this.sourceModeBlockTargets.forEach((block) => {
      // `data-source-mode` accepts a single mode (legacy) OR a space-separated
      // list (e.g., "brand_worn flatlay" for blocks shared by model-on modes).
      // `data-source-mode-except` is the complement — show for every mode
      // EXCEPT the listed ones (useful for blocks visible across most modes).
      const allowed = (block.dataset.sourceMode || "").split(/\s+/).filter(Boolean)
      const excluded = (block.dataset.sourceModeExcept || "").split(/\s+/).filter(Boolean)
      const show = excluded.length ? !excluded.includes(mode) : allowed.includes(mode)
      this.toggleVisibility(block, show)
    })

    // Per-character "Worn by" controls on garment pieces only apply to complex
    // staging (scenario) — reveal them there, hide them elsewhere.
    const scenarioOn = mode === "scenario"
    this.element.querySelectorAll("[data-scenario-only]").forEach((el) => { el.hidden = !scenarioOn })
  }

  filterPoses() {
    const gender = this.checkedRadioValue(this.genderInputTargets) || ""
    const audience = this.constructor.KID_GENDERS.includes(gender) ? "kid" : "adult"
    const positionAttr = audience === "kid" ? "kidPosition" : "adultPosition"

    if (this.hasPoseGroupTarget) {
      this.poseGroupTarget.querySelectorAll("[data-audience]").forEach((label) => {
        const aud = label.dataset.audience
        const show = aud === audience || aud === "both"
        label.hidden = !show
        label.style.order = label.dataset[positionAttr] || ""
        const cb = label.querySelector('input[type="checkbox"]')
        if (cb) {
          cb.disabled = !show
          if (!show) cb.checked = false
        }
      })
    }

    if (this.hasPoseDirectiveListTarget) {
      this.poseDirectiveListTarget.querySelectorAll("[data-audience]").forEach((li) => {
        const aud = li.dataset.audience
        li.hidden = !(aud === audience || aud === "both")
        li.style.order = li.dataset[positionAttr] || ""
      })
    }
  }

  brandCategoryChanged(event) {
    const category = event?.target?.value || null
    const detail = category ? { category, label: null } : { category: null, label: null }
    window.dispatchEvent(new CustomEvent("hero-changed", { detail }))
  }

  applyHeroFraming(event) {
    const detail = event?.detail || {}
    const category = detail.category
    const label = detail.label

    if (!this.hasPoseDirectiveListTarget) return
    const heroFramingJSON = this.poseDirectiveListTarget.dataset.heroFraming
    let heroFraming = {}
    try { heroFraming = JSON.parse(heroFramingJSON || "{}") } catch (_) { heroFraming = {} }

    this.poseDirectiveListTarget.querySelectorAll("[data-pose]").forEach((li) => {
      const pose = li.dataset.pose
      const defaultFraming = li.dataset.defaultFraming
      const override = category && heroFraming[category] && heroFraming[category][pose]
      const txt = li.querySelector("[data-framing-text]")
      if (!txt) return
      txt.textContent = override || defaultFraming
      li.style.color = override ? "var(--piktiz-green)" : ""
    })

    if (this.hasHeroBadgeTarget) {
      if (category) {
        this.heroBadgeTarget.hidden = false
        this.heroBadgeLabelTarget.textContent = label?.trim() ? `${label} (${category})` : category
      } else {
        this.heroBadgeTarget.hidden = true
      }
    }
  }

  selectAllPoses(event) {
    event.preventDefault()
    // Tick every poseInput that isn't hidden (an offsetParent of null means
    // the element or one of its ancestors is `display: none`). Works across
    // model-on poses AND the garment-only flatlay / ghost-mannequin groups.
    if (!this.hasPoseInputTarget) return
    this.poseInputTargets.forEach((cb) => {
      if (cb.disabled) return
      if (cb.offsetParent === null) return
      cb.checked = true
    })
  }

  render(industry) {
    // Industries with their OWN dedicated section in the form. Anything not in
    // this list falls back to the `data-industry="generic"` section (a plain
    // "Reference image" picker). Keep in sync with the form's data-industry
    // sections — adding a new industry section here suppresses the generic
    // fallback for that industry.
    const known = ["fashion", "food", "real_estate", "ecommerce"]
    this.sectionTargets.forEach((section) => {
      const sectionIndustry = section.dataset.industry
      let shouldShow
      if (sectionIndustry === industry) {
        shouldShow = true
      } else if (sectionIndustry === "generic" && !known.includes(industry)) {
        shouldShow = true
      } else {
        shouldShow = false
      }
      this.toggleVisibility(section, shouldShow)
    })
    if (this.hasRegenerateBtnTarget) {
      this.toggleVisibility(this.regenerateBtnTarget, known.includes(industry))
    }
  }

  // Bootstrap utilities (`d-flex`, etc.) use `display: X !important`, which
  // overrides the plain HTML `hidden` attribute. Force the toggle via an
  // inline `!important` style so the section actually disappears.
  toggleVisibility(el, show) {
    if (!el) return
    if (show) {
      el.removeAttribute("hidden")
      el.style.removeProperty("display")
    } else {
      el.setAttribute("hidden", "")
      el.style.setProperty("display", "none", "important")
    }
  }

  async regenerateSuggestion(event) {
    event?.preventDefault?.()
    if (!this.suggestUrlValue) return

    const params = new URLSearchParams()
    params.set("industry",    this.readValue("industry"))
    params.set("gender",      this.readValue("gender"))
    params.set("age",         this.readValue("age"))
    params.set("height_cm",   this.readValue("height"))
    params.set("surface",     this.readValue("surface"))
    params.set("pose",        this.readValue("pose"))
    params.set("source_mode", this.readValue("sourceMode"))

    // Food fields — Layer 1/2/3 + AI ENHANCING/CREATION mode + cuisine + style.
    params.set("food_source_mode",  this.readValue("foodMode"))
    params.set("culinary_category", this.readValue("culinaryCategory"))
    params.set("cuisine_type",      this.readValue("cuisineType"))
    params.set("restaurant_style",  this.readValue("restaurantStyle"))
    this.checkedValues('input[name="single_generation[selected_backgrounds][]"]').forEach((v) => {
      params.append("selected_backgrounds[]", v)
    })
    this.checkedValues('input[name="single_generation[selected_accessories][]"]').forEach((v) => {
      params.append("selected_accessories[]", v)
    })

    // Real-estate fields used by the suggested-prompt builder too.
    params.set("architectural_style", this.readValue("architecturalStyle"))
    params.set("property_type",       this.readValue("propertyType"))
    params.set("real_estate_plus",    this.readValue("realEstatePlus"))

    // Fashion garment_category (drives the per-pose framing on brand_worn).
    params.set("garment_category",    this.readValue("garmentCategory"))

    // Optional operator-selected camera framing/angle override.
    params.set("camera_shot",         this.readValue("cameraShot"))

    try {
      const response = await fetch(`${this.suggestUrlValue}?${params}`, {
        headers: { Accept: "text/plain" }
      })
      if (!response.ok) return
      const text = await response.text()
      if (text && this.hasPromptInputTarget) {
        this.promptInputTarget.value = text
      }
    } catch (_) {
      // best-effort — keep what's currently in the textarea
    }
  }

  readValue(name) {
    switch (name) {
      case "industry":           return this.industryInputTargets[0]?.value || ""
      case "gender":             return this.checkedRadioValue(this.genderInputTargets) || ""
      case "age":                return this.ageInputTargets[0]?.value || ""
      case "height":             return this.hasHeightInputTarget ? (this.heightInputTargets[0]?.value || "") : ""
      case "surface":            return this.surfaceInputTargets[0]?.value || ""
      case "pose":               return this.checkedRadioValue(this.poseInputTargets) || ""
      case "sourceMode":         return this.checkedRadioValue(this.sourceModeInputTargets) || ""
      case "foodMode":           return this.hasFoodModeInputTarget ? (this.checkedRadioValue(this.foodModeInputTargets) || "") : ""
      case "culinaryCategory":   return this.hasCulinaryCategoryInputTarget ? (this.culinaryCategoryInputTarget.value || "") : ""
      case "cuisineType":        return this.fieldValue('select[name="single_generation[cuisine_type]"]')
      case "restaurantStyle":    return this.fieldValue('select[name="single_generation[restaurant_style]"]')
      case "architecturalStyle": return this.checkedRadioValue(this.element.querySelectorAll('input[name="single_generation[architectural_style]"]')) || ""
      case "propertyType":       return this.checkedRadioValue(this.element.querySelectorAll('input[name="single_generation[property_type]"]')) || ""
      case "realEstatePlus":     return this.checkedRadioValue(this.element.querySelectorAll('input[name="single_generation[real_estate_plus]"]')) || ""
      case "garmentCategory":    return this.fieldValue('select[name="single_generation[garment_category]"]')
      case "cameraShot":         return this.hasCameraShotInputTarget ? (this.cameraShotInputTarget.value || "") : ""
      default: return ""
    }
  }

  fieldValue(selector) {
    const el = this.element.querySelector(selector)
    return el ? (el.value || "") : ""
  }

  checkedValues(selector) {
    return Array.from(this.element.querySelectorAll(selector))
      .filter((el) => el.checked)
      .map((el) => el.value)
  }

  checkedRadioValue(radios) {
    return Array.from(radios).find((r) => r.checked)?.value
  }
}
